/**
 * Scoring and rating calculation for tournament results.
 *
 * Maps final tournament positions to 1-5 ratings:
 * - 5: Champion (1st place)
 * - 4: Finals/Semi-finals (2nd-4th)
 * - 3: Bracket stage (5th-16th)
 * - 2: Late group elimination
 * - 1: Early/middle group elimination
 */

import type { CountryCode, Rating } from '@/data/countries/types';
import type {
  CountryRanking,
  EliminatedCountry,
  AdvancingCountry,
  TournamentGroup,
} from './types';

/**
 * Points awarded based on position within a group.
 * Position 1 (top) gets the most points, position 5 (bottom) gets the least.
 */
export function getGroupPositionPoints(position: number, groupSize: number): number {
  // Points are relative to group size
  // Position 1 = groupSize points, Position N = 1 point
  return groupSize - position + 1;
}

/**
 * Process a completed group and return eliminated and advancing countries.
 * @deprecated Use submitGroupRating in TournamentProvider instead.
 */
export function processGroupResults(
  group: TournamentGroup,
  currentRound: number,
  existingAdvancing: AdvancingCountry[]
): {
  advancing: AdvancingCountry[];
  eliminated: EliminatedCountry[];
} {
  if (!group.rankings) {
    throw new Error('Group rankings not set');
  }

  const rankings = group.rankings;
  const groupSize = rankings.length;

  // Top 2 advance, rest eliminated
  const advanceCount = Math.min(2, groupSize);

  const advancing: AdvancingCountry[] = [];
  const eliminated: EliminatedCountry[] = [];

  for (let i = 0; i < rankings.length; i++) {
    const code = rankings[i];
    const position = i + 1; // 1-indexed
    const points = getGroupPositionPoints(position, groupSize);

    // Find existing data from previous rounds
    const existing = existingAdvancing.find(c => c.code === code);
    const totalPoints = (existing?.points ?? 0) + points;
    // Convert position to star rating (1st=5, 2nd=4, etc., minimum 1)
    const stars = Math.max(1, 6 - position);

    if (position <= advanceCount) {
      advancing.push({
        code,
        stars,
        tiebreakPosition: position,
        ratedInRound: currentRound,
        points: totalPoints,
      });
    } else {
      eliminated.push({
        code,
        stars,
        tiebreakPosition: position,
        eliminatedInRound: currentRound,
        points: totalPoints,
        groupPosition: position,
      });
    }
  }

  return { advancing, eliminated };
}

/**
 * Calculate rating from bracket position.
 */
export function getBracketPositionRating(position: number): Rating {
  switch (position) {
    case 1:
      return 5; // Champion
    case 2:
      return 4; // Runner-up
    case 3:
    case 4:
      return 4; // Semi-finalists
    case 5:
    case 6:
    case 7:
    case 8:
      return 3; // Quarter-finalists
    default:
      return 3; // Other bracket participants
  }
}

/**
 * Calculate rating for a country eliminated in group stages.
 * Uses accumulated points and elimination round to determine rating.
 */
export function getGroupEliminationRating(
  country: EliminatedCountry,
  totalRounds: number,
  totalCountries: number
): Rating {
  // Calculate percentile based on points relative to maximum possible
  // Maximum points = sum of (groupSize) for each round survived
  // This is simplified: we use elimination round and position

  const roundFraction = country.eliminatedInRound / totalRounds;
  const positionPenalty = (country.groupPosition - 1) / 4; // 0-1 scale

  // Combined score: higher round = better, lower position = better
  const score = roundFraction - positionPenalty * 0.2;

  // Map to 1-2 rating range (group eliminations get 1-2)
  if (score >= 0.5) return 2;
  return 1;
}

/**
 * Calculate final rankings from tournament results.
 */
export function calculateFinalRankings(
  bracketRankings: CountryCode[],
  eliminatedInGroups: EliminatedCountry[],
  totalGroupRounds: number,
  totalCountries: number
): CountryRanking[] {
  const rankings: CountryRanking[] = [];

  // Bracket participants get positions 1-N based on bracket result
  for (let i = 0; i < bracketRankings.length; i++) {
    const position = i + 1;
    rankings.push({
      code: bracketRankings[i],
      position,
      rating: getBracketPositionRating(position),
      source: 'bracket',
    });
  }

  // Group-eliminated countries get ranked by points (descending)
  const sortedEliminated = [...eliminatedInGroups].sort((a, b) => {
    // Primary: points (higher = better)
    if (b.points !== a.points) return b.points - a.points;
    // Secondary: elimination round (later = better)
    if (b.eliminatedInRound !== a.eliminatedInRound) {
      return b.eliminatedInRound - a.eliminatedInRound;
    }
    // Tertiary: group position (lower = better)
    return a.groupPosition - b.groupPosition;
  });

  const startPosition = bracketRankings.length + 1;
  for (let i = 0; i < sortedEliminated.length; i++) {
    const country = sortedEliminated[i];
    const position = startPosition + i;

    rankings.push({
      code: country.code,
      position,
      rating: getGroupEliminationRating(country, totalGroupRounds, totalCountries),
      source: 'group',
    });
  }

  return rankings;
}

/**
 * Convert rankings to a ratings record.
 */
export function rankingsToRatings(rankings: CountryRanking[]): Record<string, number> {
  const ratings: Record<string, number> = {};
  for (const ranking of rankings) {
    ratings[ranking.code] = ranking.rating;
  }
  return ratings;
}
